import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, TextField } from '@mui/material';
import { Refresh, Search } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { toast } from 'react-toastify';
import { InvalidMessage } from "../../Utility/Constants";
import Table from '../../Table/Table';
import { PriorAuthColumns } from '../../Table/TableColumn';
import { tableColumns } from '../../Redux/TableSlice';
import { currentData, clearData, updateSearch, searchClicked, updateInputError } from '../../Redux/MNR/PriorAuthSlice';
import { clearAll, searchTriggered } from '../../Redux/CNS/CommonSlice';
import {
    validateRegexWithNumbers, validateRegexWithoutNumbers, validateRegexOnlyNumbers
} from '../../Validation/Validation';
import { GeneralError } from '../../Utility/Constants';
import Spinner from '../../Spinner/Spinner';
import { showAll, isMNR } from '../../Redux/MNR/MNRCommonSlice';

function PriorAuth() {
    const dispatch = useDispatch();
    const error = useSelector(state => state.priorAuth.error);
    const isSearched = useSelector(state => state.priorAuth.isSearchClicked);
    const searchTriggeredValue = useSelector(state => state.cnsCommon.searchTriggered);
    const inputError = useSelector(state => state.priorAuth.inputError);
    const hasAnyError = Object.values(inputError).some(value => value === true);
    const data = useSelector(state => state.priorAuth.data);
    const isSearchEnable = (
        (data.MPAGroup && data.MPAGroup.trim() !== '') ||
        (data.MpaMemberId && data.MpaMemberId.trim() !== '') ||
        (data.MPAPriorAuthorizationNumber && data.MPAPriorAuthorizationNumber.trim() !== '') ||
        (data.MPACarrier && data.MPACarrier.trim() !== '')
    );
    const loading = useSelector(state => state.priorAuth.loading);
    const showAllValue = useSelector(state => state.mnrCommon.showAll);

    useEffect(() => {
        if (error) {
            toast.error(GeneralError);
            return;
        }
    }, [error]);

    const handleResetInitial = (useCallback(() => {
        dispatch(clearAll());
        dispatch(clearData());
        dispatch(updateSearch(true));
        dispatch(searchClicked(false));
        dispatch(showAll(false));
        dispatch(isMNR(true));
    }, [dispatch]))

    const handleReset = () => {
        dispatch(clearData());
        dispatch(updateSearch(true));
        dispatch(searchClicked(false));
        dispatch(showAll(false));
    }

    useEffect(() => {
        handleResetInitial();
        dispatch(tableColumns(PriorAuthColumns));
    }, [dispatch, handleResetInitial])

    const handleChange = (name, value) => {
        const val = value.target.value || value.target.innerText;
        switch (name) {
            case 'MPAPriorAuthorizationNumber':
                //const updateRegexWithNumber = validateRegexNumbersCharSpecialCharLong(name, val, inputError);
                //dispatch(updateInputError(updateRegexWithNumber));
                break;
            case 'MPAGroup':
                const updateRegexInclNumbers = validateRegexWithNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexInclNumbers));
                break;
            case 'MPACarrier':
                const updateRegexOnlyChar = validateRegexWithoutNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexOnlyChar));
                break;
            case 'MpaMemberId':
                const updateRegexOnlyNumber = validateRegexOnlyNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexOnlyNumber));
                break;
            default:
                break;
        }

        const updatedData = {
            ...data,
            [name]: val
        };
        dispatch(currentData(updatedData));
    }

    const handleSearch = () => {
        dispatch(showAll(false));
        dispatch(searchClicked(true));
        dispatch(searchTriggered(!searchTriggeredValue));
    }

    const handleShowAll = () => {
        dispatch(showAll(true));
        dispatch(clearData());
        dispatch(searchClicked(false));
        dispatch(searchTriggered(!searchTriggeredValue));
    }

    return (
        <>
            {loading && <Spinner />}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="memberID"
                        label="MemberID"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('MpaMemberId', e)}
                        value={data.MpaMemberId}
                        error={inputError.MpaMemberId}
                        helperText={inputError.MpaMemberId ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="carrier"
                        label="Carrier"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('MPACarrier', e)}
                        value={data.MPACarrier}
                        error={inputError.MPACarrier}
                        helperText={inputError.MPACarrier ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="group"
                        label="Group"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('MPAGroup', e)}
                        value={data.MPAGroup}
                        error={inputError.MPAGroup}
                        helperText={inputError.MPAGroup ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="paNumber"
                        label="PANumber"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('MPAPriorAuthorizationNumber', e)}
                        value={data.MPAPriorAuthorizationNumber}
                        error={inputError.MPAPriorAuthorizationNumber}
                        helperText={inputError.MPAPriorAuthorizationNumber ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3} sx={{ justifyContent: 'center', paddingTop: '24px' }}>
                <Button
                    variant="contained"
                    onClick={handleShowAll}
                >
                    Show All
                </Button>
                <Button
                    variant={(!isSearched && !showAllValue) ? "contained" : "outlined"}
                    style={isSearched ? { color: 'var(--primary-font-color)' } : undefined}
                    size="small"
                    onClick={handleReset}
                    endIcon={<Refresh />}
                    disabled={!isSearched && !showAllValue}
                >
                    Reset
                </Button>
                <Button
                    variant={(!isSearchEnable || hasAnyError) ? "contained" : "contained"}
                    size="large"
                    onClick={handleSearch}
                    endIcon={<Search />}
                    style={(isSearchEnable && !hasAnyError) ? { color: 'var(--primary-font-color)', background: 'var(--primary-color)', border: '1px solid var(--primary-font-color)' } : undefined}
                    disabled={!isSearchEnable || hasAnyError}
                >
                    Search
                </Button>
            </Grid>
            {((isSearched) || (showAllValue)) && <Table />}
        </>
    )
}

export default PriorAuth;